<template>
  <div class="w-full relative">
    <label v-if="label" :for="name" class="block mb-1 text-sm font-medium text-gray-700">
      {{ label }} <span v-if="required && !disabled" class="text-red-500">*</span>
    </label>

    <div class="relative">
      <input :id="name" v-model="internalValue" v-maska="mask" :name="name" :type="showPassword ? 'text' : type"
        :placeholder="placeholder"
        :inputmode="inputmode || (type === 'number' || type === 'decimal' ? 'decimal' : (type === 'tel' ? 'tel' : null))"
        :pattern="type === 'number' || type === 'decimal' ? '[0-9]*[.]?[0-9]*' : null" :class="[
          baseClass,
          error ? 'border-red-500' : 'border-gray-600',
          customClass,
          {
            '[appearance:textfield]': type === 'number',
            '[&::-webkit-outer-spin-button]:appearance-none': type === 'number',
            '[&::-webkit-inner-spin-button]:appearance-none': type === 'number'
          }
        ]" :disabled="disabled" :maxlength="length" @wheel.prevent @keydown="onKeyDown" @blur="onBlur"
        @input="onInputPassword">

      <!-- Eye toggle icon -->
      <button v-if="type === 'password'" type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-[#666]"
        tabindex="-1" @click="togglePassword">
        <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"
          class="h-[26px] w-[26px]">
          <path d="M0 0h24v24H0z" fill="none" />
          <g fill="none" stroke="currentColor" stroke-linecap="round">
            <path stroke-linejoin="round"
              d="M10.73 5.073A11 11 0 0 1 12 5c4.664 0 8.4 2.903 10 7a11.6 11.6 0 0 1-1.555 2.788M6.52 6.519C4.48 7.764 2.9 9.693 2 12c1.6 4.097 5.336 7 10 7a10.44 10.44 0 0 0 5.48-1.52m-7.6-7.6a3 3 0 1 0 4.243 4.243" />
            <path d="m4 4l16 16" />
          </g>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 256 256"
          class="h-[26px] w-[26px]">
          <path d="M0 0h256v256H0z" fill="none" />
          <path fill="currentColor"
            d="M243.66 126.38c-.34-.76-8.52-18.89-26.83-37.2C199.87 72.22 170.7 52 128 52S56.13 72.22 39.17 89.18c-18.31 18.31-26.49 36.44-26.83 37.2a4.08 4.08 0 0 0 0 3.25c.34.77 8.52 18.89 26.83 37.2c17 17 46.14 37.17 88.83 37.17s71.87-20.21 88.83-37.17c18.31-18.31 26.49-36.43 26.83-37.2a4.08 4.08 0 0 0 0-3.25m-32.7 35c-23.07 23-51 34.62-83 34.62s-59.89-11.65-83-34.62A135.7 135.7 0 0 1 20.44 128A135.7 135.7 0 0 1 45 94.62C68.11 71.65 96 60 128 60s59.89 11.65 83 34.62A135.8 135.8 0 0 1 235.56 128A135.7 135.7 0 0 1 211 161.38ZM128 84a44 44 0 1 0 44 44a44.05 44.05 0 0 0-44-44m0 80a36 36 0 1 1 36-36a36 36 0 0 1-36 36" />
        </svg>

      </button>
    </div>

    <p v-if="error && !disabled" class="text-red-500 text-xs mt-1 mb-0">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, watch, defineExpose } from 'vue';
import { vMaska } from 'maska/vue';

const props = defineProps({
  modelValue: {},
  name: String,
  label: String,
  type: {
    type: String,
    default: 'text'
  },
  placeholder: String,
  required: {
    type: Boolean,
    default: false
  },
  regex: RegExp,
  errorMessage: String,
  // shown when a required field is empty (errorMessage is for invalid values)
  requiredMessage: String,
  mask: String,
  customClass: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  confirmWith: {
    type: String,
    default: ''
  },
  length: {
    type: String,
    default: null
  },
  inputmode: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'input']); // ✅ Declare both events

const internalValue = ref(props.modelValue || '');
const error = ref('');
const touched = ref(false);
const showPassword = ref(false);

const baseClass = 'w-full mobile:p-1.5 largeDesktop:p-2 !pr-[35px] mobile:rounded-md largeDesktop:rounded-[5px] border outline-none text-sm !text-[#333] disabled:cursor-not-allowed disabled:!bg-[#EFEFEF] disabled:!text-[#666] !appearance-none';

watch(() => props.modelValue, (val) => {
  internalValue.value = val || '';
});

watch(internalValue, (val) => {
  emit('update:modelValue', val);
  if (touched.value && !props.disabled) {
    validate();
  }
});

function onBlur() {
  touched.value = true;
  validate();
}

function onKeyDown(e) {
  // Prevent arrow keys
  if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
    e.preventDefault();
  }

  if (props.length &&
    e.target.value.length >= props.length &&
    e.key.length === 1 && // only characters, not Backspace/Delete
    !e.ctrlKey && !e.metaKey
  ) {
    e.preventDefault();
  }
}

function blockPaste(e) {
  if (props.type == 'password') {
    e.preventDefault()
  }
}

function onInputPassword(e) {
  let val = e.target.value
  if (props.type === 'password') {
    // Remove emojis
    val = val.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '')
    internalValue.value = val
  } else {
    onInput(e) // normal input for other types
  }
}

function onInput(event) {
  let value = event.target.value;
  emit("input", value);
  validate();
}

function togglePassword() {
  showPassword.value = !showPassword.value;
}

function validate() {
  if (props.disabled) return '';
  let trimmed = ''
  let digitsOnly = ''
  if (props.type == 'number') {
    trimmed = internalValue.value || '';
  } else {
    trimmed = internalValue.value?.trim() || '';
    digitsOnly = trimmed.replace(/\D/g, '');
  }

  if (props.required && trimmed === '') {
    error.value = props.requiredMessage || 'This field is required.';
    return error.value;
  }

  if (props.mask === '(###) ###-####' && (internalValue.value.length > 0 && digitsOnly.length !== 10)) {
    error.value = props.errorMessage || 'Invalid phone number.';
    return error.value;
  }

  if (trimmed !== '' && props.regex && !props.regex.test(trimmed)) {
    error.value = props.errorMessage || 'Invalid input.';
    return error.value;
  }

  if (props.confirmWith && props.confirmWith !== trimmed) {
    error.value = props.errorMessage || 'Passwords do not match.';
    return error.value;
  }

  error.value = '';
  return '';
}

function resetField() {
  error.value = ''
}

defineExpose({
  resetField,
  validate,
  hasError: () => !!error.value
});
</script>
